import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { useAuthStore } from '../../store/authStore';
import { useRadioStore } from '../../store/radioStore';
import { apiClient } from '../../api/client';
import { businessProfilesApi } from '../../api/business-profiles';
import { radioApi } from '../../api/radio';
import { resumeAudioContext } from '../../store/radioStore';
import AudioVisualizer from '../../components/AudioVisualizer';

type Screen = 'auth' | 'profiles' | 'moods' | 'player';

interface BusinessProfile {
  _id: string;
  slug: string;
  displayName: string;
  icon?: string;
  moodCategories?: string[];
}

interface DeviceCodeResponse {
  device_code: string;
  user_code: string;
  verification_uri: string;
  verification_uri_complete: string;
  expires_in: number;
  interval: number;
}

interface TvCommand {
  type: 'command';
  command: 'play' | 'pause' | 'setMood' | 'setVolume' | 'skip' | 'toggleVisualizer';
  mood?: string;
  profileSlug?: string;
  volume?: number;
}

export default function TvAppPage() {
  const { t } = useTranslation();
  const { isAuthenticated, fetchCurrentUser } = useAuthStore();
  const {
    currentTrack,
    nextTrack,
    isPlaying,
    isLoading: radioLoading,
    elapsed,
    remaining,
    duration,
    startRadio,
    stopRadio,
    pause,
    resume,
    setVolume,
  } = useRadioStore();

  const [screen, setScreen] = useState<Screen>('auth');
  const [deviceCode, setDeviceCode] = useState<DeviceCodeResponse | null>(null);
  const [profiles, setProfiles] = useState<BusinessProfile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<BusinessProfile | null>(null);
  const [moods, setMoods] = useState<string[]>([]);
  const [selectedMood, setSelectedMood] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [isPolling, setIsPolling] = useState(false);
  const [sessionCode, setSessionCode] = useState<string | null>(null);
  const [volume, setVolumeState] = useState(100);
  const [pendingMood, setPendingMood] = useState<{ profileSlug: string; mood: string; profile: BusinessProfile } | null>(null);
  const [pendingPlay, setPendingPlay] = useState(false);
  const [, setUserInteracted] = useState(false);
  const [fullscreenVisualizer, setFullscreenVisualizer] = useState(false);
  const userInteractedRef = useRef(false);
  const profilesRef = useRef<BusinessProfile[]>([]);

  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const eventSourceRef = useRef<EventSource | null>(null);
  const stateUpdateInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  // Helper to set ref without returning value
  const setItemRef = (index: number) => (el: HTMLButtonElement | null) => {
    itemRefs.current[index] = el;
  };

  // Get or create persistent device ID
  const getDeviceId = (): string => {
    const storageKey = 'fonorex_tv_device_id';
    let deviceId = localStorage.getItem(storageKey);
    if (!deviceId) {
      // Generate UUID-like device ID (fallback for non-secure contexts)
      const generateId = () => {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
          return crypto.randomUUID();
        }
        // Fallback for older browsers or non-HTTPS
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
      };
      deviceId = 'tv_' + generateId();
      localStorage.setItem(storageKey, deviceId);
      console.log('Generated new device ID:', deviceId);
    }
    return deviceId;
  };

  // Get stored device name
  const getDeviceName = (): string => {
    return localStorage.getItem('fonorex_tv_device_name') || 'Web TV';
  };

  // Create TV session and connect to SSE
  const createTvSession = async () => {
    try {
      const deviceId = getDeviceId();
      const deviceName = getDeviceName();

      const response = await apiClient.post<{
        sessionCode: string;
        deviceId: string;
        profileSlug?: string;
        mood?: string;
        volume?: number;
      }>('/tv-sessions', {
        deviceId,
        deviceName,
      });
      const { sessionCode: code, profileSlug, mood, volume } = response.data;
      setSessionCode(code);
      console.log('TV Session:', code, '(device:', deviceId, '), saved state:', { profileSlug, mood });

      // Restore volume if saved
      if (volume !== undefined) {
        setVolumeState(volume);
        setVolume(volume / 100);
      }

      // If session has saved profile/mood, restore it
      if (profileSlug && mood) {
        console.log('Restoring saved session state:', profileSlug, mood);
        // Need to find the profile object
        let profile = profilesRef.current.find(p => p.slug === profileSlug);
        if (!profile) {
          // Profiles might not be loaded yet, fetch them
          const allProfiles = await businessProfilesApi.getAll({ active: true });
          setProfiles(allProfiles);
          profilesRef.current = allProfiles;
          profile = allProfiles.find(p => p.slug === profileSlug);
        }
        if (profile) {
          // Show pending mood overlay (user needs to click to start due to autoplay policy)
          setPendingMood({ profileSlug, mood, profile });
          setScreen('player');
        }
      }

      // Connect to SSE for receiving commands
      connectToSSE(code);
    } catch (error) {
      console.error('Failed to create TV session:', error);
    }
  };

  // Connect to SSE for receiving remote commands
  const connectToSSE = (code: string) => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Get token from localStorage for SSE auth (EventSource doesn't support headers)
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      console.error('No auth token for SSE connection');
      return;
    }

    const url = `/api/v1/tv-sessions/${code}/events/tv?token=${encodeURIComponent(authToken)}`;
    console.log('Connecting to SSE:', url.replace(authToken, 'TOKEN'));

    const eventSource = new EventSource(url);

    eventSource.onopen = () => {
      console.log('SSE connected for TV commands, readyState:', eventSource.readyState);
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as TvCommand | { type: 'connected' };
        console.log('SSE message:', data);

        if (data.type === 'command') {
          handleRemoteCommand(data);
        }
      } catch (e) {
        console.error('Failed to parse SSE message:', e);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE error, readyState:', eventSource.readyState, error);
      if (eventSource.readyState === EventSource.CLOSED) {
        // Check if token is still valid before trying to reconnect
        apiClient.get('/auth/me').then(() => {
          // Token is valid, just reconnect
          setTimeout(() => {
            console.log('Reconnecting SSE for code:', code);
            connectToSSE(code);
          }, 5000);
        }).catch((err) => {
          if (err.response?.status === 401) {
            // Token expired - dispatch event to reset to QR login
            console.log('SSE closed due to auth error, triggering re-auth');
            window.dispatchEvent(new CustomEvent('api-401-error', { detail: { path: '/tv/app' } }));
          } else {
            // Other error, try to reconnect
            setTimeout(() => {
              console.log('Reconnecting SSE for code:', code);
              connectToSSE(code);
            }, 5000);
          }
        });
      }
    };

    eventSourceRef.current = eventSource;
  };

  // Handle remote commands
  const handleRemoteCommand = async (cmd: TvCommand) => {
    console.log('Handling remote command:', cmd.command, cmd);
    switch (cmd.command) {
      case 'play': {
        // Check if there's actually something to play
        const radioState = useRadioStore.getState();
        if (!radioState.currentTrack) {
          console.log('Play command received but no track loaded - ignoring');
          break;
        }
        if (userInteractedRef.current) {
          resume();
        } else {
          // Browser autoplay policy - need user interaction first
          setPendingPlay(true);
        }
        break;
      }
      case 'pause':
        pause();
        break;
      case 'setVolume':
        if (cmd.volume !== undefined) {
          setVolumeState(cmd.volume);
          setVolume(cmd.volume / 100);
        }
        break;
      case 'setMood':
        if (cmd.profileSlug && cmd.mood) {
          console.log('Setting mood:', cmd.profileSlug, cmd.mood, 'profiles in ref:', profilesRef.current.length);
          // Find profile by slug using ref (always current value)
          let profile = profilesRef.current.find(p => p.slug === cmd.profileSlug);
          if (!profile) {
            // Profiles might not be loaded yet, fetch them
            console.log('Profile not in cache, fetching...');
            try {
              const allProfiles = await businessProfilesApi.getAll({ active: true });
              setProfiles(allProfiles);
              profilesRef.current = allProfiles;
              profile = allProfiles.find(p => p.slug === cmd.profileSlug);
            } catch (e) {
              console.error('Failed to fetch profiles:', e);
            }
          }
          if (profile) {
            console.log('Found profile, starting radio:', profile.displayName, 'userInteracted:', userInteractedRef.current);
            // Check if user has interacted with the page (browser autoplay policy)
            if (userInteractedRef.current) {
              setSelectedProfile(profile);
              setSelectedMood(cmd.mood);
              // startRadio handles cleanup internally
              startRadio(cmd.profileSlug, cmd.mood);
              setScreen('player');
            } else {
              // Save pending mood and show prompt to user
              console.log('User has not interacted yet, saving pending mood');
              setPendingMood({ profileSlug: cmd.profileSlug, mood: cmd.mood, profile });
              setScreen('player');
            }
          } else {
            console.error('Profile not found:', cmd.profileSlug);
          }
        }
        break;
      case 'skip':
        // Radio doesn't support skip, but we could implement it
        break;
      case 'toggleVisualizer':
        setFullscreenVisualizer(prev => !prev);
        break;
    }
  };

  // Send state updates to remote controllers
  const sendStateUpdate = async () => {
    if (!sessionCode) return;

    try {
      await apiClient.put(`/tv-sessions/${sessionCode}/state`, {
        profileSlug: selectedProfile?.slug,
        mood: selectedMood,
        isPlaying,
        volume,
        currentTrackId: currentTrack?._id,
        currentTrackName: currentTrack?.name,
        elapsed,
        duration,
      });
    } catch (error) {
      // Silent fail for state updates
    }
  };

  // Update state periodically when playing
  useEffect(() => {
    if (sessionCode && screen === 'player') {
      // Send initial state
      sendStateUpdate();

      // Update every 5 seconds
      stateUpdateInterval.current = setInterval(sendStateUpdate, 5000);

      return () => {
        if (stateUpdateInterval.current) {
          clearInterval(stateUpdateInterval.current);
        }
      };
    }
  }, [sessionCode, screen, isPlaying, currentTrack, elapsed, selectedMood, volume]);

  // Handle 401 errors - reset to auth screen
  const handleAuthError = useCallback(() => {
    console.log('Auth error detected, resetting to QR login');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    useAuthStore.setState({ token: null, isAuthenticated: false, user: null });
    stopRadio();
    setScreen('auth');
    setSessionCode(null);
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    requestDeviceCode();
  }, [stopRadio]);

  // Check auth status on mount
  useEffect(() => {
    if (isAuthenticated) {
      setScreen('profiles');
      loadProfiles();
      createTvSession();
    } else {
      requestDeviceCode();
    }

    // Listen for 401 errors via custom event (for TV app)
    const handle401 = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.path?.startsWith('/tv/')) {
        handleAuthError();
      }
    };
    window.addEventListener('api-401-error', handle401);

    return () => {
      window.removeEventListener('api-401-error', handle401);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (stateUpdateInterval.current) {
        clearInterval(stateUpdateInterval.current);
      }
    };
  }, [isAuthenticated, handleAuthError]);

  // Request device code for QR auth
  const requestDeviceCode = async () => {
    try {
      const response = await apiClient.post<DeviceCodeResponse>('/auth/device/code');
      setDeviceCode(response.data);
      setIsPolling(true);
    } catch (error) {
      console.error('Failed to get device code:', error);
    }
  };

  // Poll for authorization
  useEffect(() => {
    if (!isPolling || !deviceCode) return;

    const interval = setInterval(async () => {
      try {
        const response = await apiClient.get<{ status: string; token?: string; refresh_token?: string }>(
          `/auth/device/token?device_code=${deviceCode.device_code}`
        );

        if (response.data.status === 'authorized' && response.data.token) {
          setIsPolling(false);
          localStorage.setItem('authToken', response.data.token);
          if (response.data.refresh_token) {
            localStorage.setItem('refreshToken', response.data.refresh_token);
          }
          useAuthStore.setState({ token: response.data.token, isAuthenticated: true });
          await fetchCurrentUser();
          setScreen('profiles');
          loadProfiles();
          createTvSession();
        } else if (response.data.status === 'expired') {
          setIsPolling(false);
          requestDeviceCode();
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, (deviceCode.interval || 5) * 1000);

    return () => clearInterval(interval);
  }, [isPolling, deviceCode]);

  // Load business profiles
  const loadProfiles = async () => {
    try {
      const data = await businessProfilesApi.getAll({ active: true });
      setProfiles(data);
      profilesRef.current = data;
      setFocusedIndex(0);
    } catch (error) {
      console.error('Failed to load profiles:', error);
    }
  };

  // Load moods for selected profile
  const loadMoods = async (profile: BusinessProfile) => {
    try {
      const data = await radioApi.getAvailableMoods(profile.slug);
      setMoods(data.moods);
      setFocusedIndex(0);
    } catch (error) {
      console.error('Failed to load moods:', error);
      // Fallback to profile's mood categories
      setMoods(profile.moodCategories || []);
    }
  };

  // Handle profile selection
  const handleProfileSelect = (profile: BusinessProfile) => {
    setSelectedProfile(profile);
    loadMoods(profile);
    setScreen('moods');
  };

  // Handle mood selection
  const handleMoodSelect = (mood: string) => {
    if (selectedProfile) {
      setUserInteracted(true);
      userInteractedRef.current = true;
      setPendingMood(null);
      setSelectedMood(mood);
      // Resume audio context on user interaction (required by browsers)
      resumeAudioContext();
      startRadio(selectedProfile.slug, mood);
      setScreen('player');
    }
  };

  // Handle pending mood activation (user clicks to start after remote command)
  const handleActivatePendingMood = () => {
    if (pendingMood) {
      setUserInteracted(true);
      userInteractedRef.current = true;
      setSelectedProfile(pendingMood.profile);
      setSelectedMood(pendingMood.mood);
      // Resume audio context on user interaction (required by browsers)
      resumeAudioContext();
      startRadio(pendingMood.profileSlug, pendingMood.mood);
      setPendingMood(null);
    }
  };

  // Handle pending play activation (user clicks to enable playback after remote play command)
  const handleActivatePendingPlay = () => {
    setUserInteracted(true);
    userInteractedRef.current = true;
    setPendingPlay(false);
    // Resume audio context on user interaction (required by browsers)
    resumeAudioContext();
    resume();
  };

  // Handle back navigation
  const handleBack = () => {
    if (screen === 'player') {
      stopRadio();
      setScreen('moods');
      setFocusedIndex(0);
    } else if (screen === 'moods') {
      setScreen('profiles');
      setFocusedIndex(0);
    }
  };

  // Keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Handle pending play overlay - any key activates playback
    if (pendingPlay) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
        e.preventDefault();
        handleActivatePendingPlay();
      }
      return;
    }

    // Handle fullscreen visualizer exit
    if (fullscreenVisualizer) {
      if (e.key === 'Escape' || e.key === 'Backspace' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setFullscreenVisualizer(false);
      }
      return;
    }

    const items = screen === 'profiles' ? profiles : screen === 'moods' ? moods : [];
    const maxIndex = items.length - 1;

    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        setFocusedIndex(prev => Math.max(0, prev - 1));
        break;
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault();
        setFocusedIndex(prev => Math.min(maxIndex, prev + 1));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (screen === 'profiles' && profiles[focusedIndex]) {
          handleProfileSelect(profiles[focusedIndex]);
        } else if (screen === 'moods' && moods[focusedIndex]) {
          handleMoodSelect(moods[focusedIndex]);
        } else if (screen === 'player') {
          if (pendingMood) {
            handleActivatePendingMood();
          } else if (isPlaying) {
            pause();
          } else {
            resume();
          }
        }
        break;
      case 'Backspace':
      case 'Escape':
        e.preventDefault();
        handleBack();
        break;
    }
  }, [screen, profiles, moods, focusedIndex, isPlaying, pendingMood, pendingPlay, fullscreenVisualizer]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Focus management
  useEffect(() => {
    if (itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex]?.focus();
    }
  }, [focusedIndex, screen]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatMoodName = (mood: string): string => {
    return mood.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Pending play overlay - shown when remote sent play but user hasn't interacted yet
  if (pendingPlay) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 flex items-center justify-center p-8 cursor-pointer"
        onClick={handleActivatePendingPlay}
      >
        <div className="text-center max-w-xl">
          <div className="w-32 h-32 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white shadow-lg shadow-primary-500/30 mx-auto mb-8 animate-pulse">
            <svg className="w-16 h-16 ml-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">
            {t('tv.clickToEnablePlayback', 'Click anywhere to enable playback')}
          </h2>
          <p className="text-xl text-gray-400">
            {t('tv.browserRequiresInteraction', 'Browser requires user interaction to play audio')}
          </p>
        </div>
      </div>
    );
  }

  // Auth Screen with QR Code
  if (screen === 'auth') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 flex items-center justify-center p-8">
        <div className="text-center max-w-xl">
          <h1 className="text-5xl font-bold text-white mb-4">Fonorex</h1>
          <p className="text-2xl text-gray-400 mb-12">
            {t('tv.scanToLogin', 'Scan QR code with your phone to sign in')}
          </p>

          {deviceCode ? (
            <div className="bg-white p-8 rounded-3xl inline-block mb-8">
              <QRCodeSVG
                value={deviceCode.verification_uri_complete}
                size={280}
                level="M"
              />
            </div>
          ) : (
            <div className="w-72 h-72 mx-auto bg-white/10 rounded-3xl flex items-center justify-center mb-8">
              <div className="w-12 h-12 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
            </div>
          )}

          <p className="text-xl text-gray-500 mb-4">
            {t('tv.orEnterCode', 'Or enter code at')}
          </p>
          <p className="text-2xl text-primary-400 font-mono mb-4">
            fonorex.com/tv
          </p>
          {deviceCode && (
            <p className="text-4xl text-white font-mono tracking-widest">
              {deviceCode.user_code}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Profiles Screen
  if (screen === 'profiles') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 p-12">
        <div className="flex justify-between items-start mb-12">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Fonorex</h1>
            <p className="text-xl text-gray-400">
              {t('tv.selectProfile', 'Select your business type')}
            </p>
          </div>
          {sessionCode && (
            <div className="flex items-center gap-4">
              <div className="bg-white p-2 rounded-lg">
                <QRCodeSVG
                  value={`${window.location.origin}/tv/remote/${sessionCode}`}
                  size={64}
                  level="M"
                />
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500 mb-1">{t('tv.remoteCode', 'Remote code')}</p>
                <p className="text-2xl text-primary-400 font-mono">{sessionCode}</p>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {profiles.map((profile, index) => (
            <button
              key={profile._id}
              ref={setItemRef(index)}
              onClick={() => handleProfileSelect(profile)}
              className={`p-8 rounded-2xl text-left transition-all duration-200 ${
                focusedIndex === index
                  ? 'bg-primary-500 scale-105 ring-4 ring-primary-400'
                  : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              <span className="text-5xl mb-4 block">{profile.icon || '🎵'}</span>
              <span className="text-2xl font-semibold text-white block">
                {profile.displayName}
              </span>
            </button>
          ))}
        </div>

        <p className="text-gray-500 mt-12 text-lg">
          {t('tv.navHint', 'Use arrow keys to navigate, Enter to select')}
        </p>
      </div>
    );
  }

  // Moods Screen
  if (screen === 'moods') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 p-12">
        <div className="flex justify-between items-start mb-8">
          <button
            onClick={handleBack}
            className="text-gray-400 hover:text-white text-xl flex items-center gap-2"
          >
            ← {t('tv.back', 'Back')}
          </button>
          {sessionCode && (
            <div className="flex items-center gap-4">
              <div className="bg-white p-2 rounded-lg">
                <QRCodeSVG
                  value={`${window.location.origin}/tv/remote/${sessionCode}`}
                  size={64}
                  level="M"
                />
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500 mb-1">{t('tv.remoteCode', 'Remote code')}</p>
                <p className="text-2xl text-primary-400 font-mono">{sessionCode}</p>
              </div>
            </div>
          )}
        </div>

        <h1 className="text-4xl font-bold text-white mb-2">
          {selectedProfile?.icon} {selectedProfile?.displayName}
        </h1>
        <p className="text-xl text-gray-400 mb-12">
          {t('tv.selectMood', 'Select mood')}
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {moods.map((mood, index) => (
            <button
              key={mood}
              ref={setItemRef(index)}
              onClick={() => handleMoodSelect(mood)}
              className={`p-8 rounded-2xl text-left transition-all duration-200 ${
                focusedIndex === index
                  ? 'bg-primary-500 scale-105 ring-4 ring-primary-400'
                  : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              <span className="text-2xl font-semibold text-white">
                {formatMoodName(mood)}
              </span>
            </button>
          ))}
        </div>

        <p className="text-gray-500 mt-12 text-lg">
          {t('tv.navHint', 'Use arrow keys to navigate, Enter to select')}
        </p>
      </div>
    );
  }

  // Player Screen
  return (
    <>
      {/* Fullscreen visualizer overlay - renders on top without unmounting player */}
      {fullscreenVisualizer && (
        <div
          className="fixed inset-0 bg-black cursor-pointer z-50"
          onClick={() => setFullscreenVisualizer(false)}
        >
          <AudioVisualizer isPlaying={isPlaying} />
          {/* Minimal track info overlay */}
          <div className="absolute bottom-8 left-0 right-0 text-center opacity-50 hover:opacity-100 transition-opacity">
            <p className="text-white text-2xl font-medium">{currentTrack?.name}</p>
            <p className="text-gray-400 text-lg mt-1">
              {selectedProfile?.displayName} · {selectedMood && formatMoodName(selectedMood)}
            </p>
          </div>
          {/* Exit hint */}
          <div className="absolute top-6 right-6 text-gray-500 text-sm opacity-0 hover:opacity-100 transition-opacity">
            {t('tv.pressToExit', 'Press any key to exit')}
          </div>
        </div>
      )}
      <div className={fullscreenVisualizer ? 'invisible' : ''}>
      {/* Main player content */}
    <div className="min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 flex items-center justify-center p-12">
      <div className="w-full max-w-3xl">
        {/* Header with back and session code */}
        <div className="flex justify-between items-start mb-8">
          <button
            onClick={handleBack}
            className="text-gray-400 hover:text-white text-xl flex items-center gap-2"
          >
            ← {t('tv.back', 'Back')}
          </button>
          {sessionCode && (
            <div className="flex items-center gap-4">
              <div className="bg-white p-2 rounded-lg">
                <QRCodeSVG
                  value={`${window.location.origin}/tv/remote/${sessionCode}`}
                  size={64}
                  level="M"
                />
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500 mb-1">{t('tv.remoteCode', 'Remote code')}</p>
                <p className="text-2xl text-primary-400 font-mono">{sessionCode}</p>
              </div>
            </div>
          )}
        </div>

        {/* Radio indicator */}
        <div className="flex items-center gap-3 mb-8">
          <span className="relative flex h-4 w-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-4 w-4 bg-primary-500"></span>
          </span>
          <span className="text-primary-300 font-medium text-xl">
            {t('radio.live', 'LIVE')} — {selectedProfile?.displayName} / {selectedMood && formatMoodName(selectedMood)}
          </span>
        </div>

        {/* Pending mood - waiting for user interaction */}
        {pendingMood && !currentTrack && (
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-12 text-center">
            <p className="text-xl text-gray-400 mb-4">
              {t('tv.remoteSelected', 'Remote selected')}
            </p>
            <h2 className="text-3xl font-bold text-white mb-2">
              {pendingMood.profile.icon} {pendingMood.profile.displayName}
            </h2>
            <p className="text-2xl text-primary-400 mb-10">
              {formatMoodName(pendingMood.mood)}
            </p>
            <button
              onClick={handleActivatePendingMood}
              className="w-32 h-32 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white shadow-lg shadow-primary-500/30 hover:scale-105 transition-transform focus:ring-4 focus:ring-primary-400 mx-auto"
              autoFocus
            >
              <svg className="w-16 h-16 ml-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>
            <p className="text-gray-500 mt-8 text-lg">
              {t('tv.pressToStart', 'Press to start playback')}
            </p>
          </div>
        )}

        {/* Loading */}
        {radioLoading && !pendingMood && (
          <div className="text-center py-20">
            <div className="w-16 h-16 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin mx-auto mb-6" />
            <p className="text-2xl text-gray-400">{t('radio.loading', 'Connecting...')}</p>
          </div>
        )}

        {/* Player */}
        {!radioLoading && currentTrack && (
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-12">
            {/* Track info */}
            <div className="text-center mb-10">
              <p className="text-lg text-primary-400 mb-2">{t('radio.nowPlaying', 'Now Playing')}</p>
              <h2 className="text-4xl font-bold text-white">{currentTrack.name}</h2>
            </div>

            {/* Progress */}
            <div className="mb-10">
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full transition-all duration-1000"
                  style={{ width: `${duration > 0 ? (elapsed / duration) * 100 : 0}%` }}
                />
              </div>
              <div className="flex justify-between mt-3 text-lg text-gray-400">
                <span>{formatTime(elapsed)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Play/Pause and Visualizer buttons */}
            <div className="flex justify-center items-center gap-6 mb-10">
              <button
                onClick={() => {
                  resumeAudioContext();
                  isPlaying ? pause() : resume();
                }}
                className="w-24 h-24 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white shadow-lg shadow-primary-500/30 hover:scale-105 transition-transform focus:ring-4 focus:ring-primary-400"
                autoFocus
              >
                {isPlaying ? (
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                  </svg>
                ) : (
                  <svg className="w-12 h-12 ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>

              {/* Fullscreen Visualizer button */}
              <button
                onClick={() => setFullscreenVisualizer(true)}
                className="w-16 h-16 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-all hover:scale-105 focus:ring-4 focus:ring-white/20"
                title={t('tv.visualizer', 'Visualizer')}
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
              </button>
            </div>

            {/* Volume indicator */}
            <div className="flex items-center justify-center gap-4 mb-10">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
              </svg>
              <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white/50 rounded-full"
                  style={{ width: `${volume}%` }}
                />
              </div>
              <span className="text-gray-400 w-12">{volume}%</span>
            </div>

            {/* Next track */}
            {nextTrack && (
              <div className="border-t border-white/10 pt-8">
                <p className="text-lg text-gray-400 mb-3">
                  {t('radio.upNext', 'Up Next')} ({t('radio.in', 'in')} {formatTime(remaining)})
                </p>
                <p className="text-2xl text-white">{nextTrack.name}</p>
              </div>
            )}
          </div>
        )}

        <p className="text-gray-500 mt-10 text-center text-lg">
          {t('tv.playerHint', 'Press Enter or Space to play/pause, Backspace to go back')}
        </p>
      </div>
    </div>
      </div>
    </>
  );
}
